import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { cn } from "@/lib/utils";

interface LoadingPageProps {
  /** Use when the loading state is inside a layout with sidebar (e.g. dashboard). */
  className?: string;
  /** Optional short headline above the spinner ("Cargando tu solicitud"). */
  title?: string;
  /**
   * Optional one-sentence description below the spinner. Use to set expectations when
   * the load can take a few seconds ("Buscamos los detalles y los documentos asociados").
   * Reduces anxiety on slower fetches like multi-file document lists.
   */
  description?: string;
}

/**
 * Full-page loading state. Use whenever a page is loading its main data so users
 * see the branded loading experience instead of a plain "Loading…" title or text.
 * Renders a centered LoadingLogo with consistent min-height.
 */
export function LoadingPage({ className, title, description }: LoadingPageProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={title ?? "Loading"}
      className={cn(
        "flex min-h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center",
        className
      )}
    >
      <LoadingLogo size="lg" />
      {title ? (
        <p className="mt-1 text-base font-medium text-foreground">{title}</p>
      ) : null}
      {description ? (
        <p className="max-w-xs text-sm text-muted-foreground">{description}</p>
      ) : null}
    </div>
  );
}
